import { router } from "@inertiajs/react"
import { useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent } from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import type { FunnelRangePreset } from "@/types/invites"

/**
 * OKS-21 — Date-range filter for the funnel widget.
 *
 * Three presets: **Last 7 days** / **Last 30 days** / **Custom**.
 * Renders as shadcn `<Tabs>`; `Custom` opens a popover with two
 * native `<input type="date">` fields + Apply button.
 *
 * **Partial reload, no new route.** Backend's `CandidatesController#index`
 * exposes `funnel` as a deferred Inertia prop driven by `?from=YYYY-MM-DD&to=YYYY-MM-DD`.
 * Selecting a preset triggers `router.reload({ only: ["funnel"], data: ... })` —
 * the candidates table + filter rail stay mounted; only the funnel
 * card refreshes.
 *
 * **No `localStorage` / `sessionStorage`** — Crew project rule. The
 * preset state lives in the URL (controller echoes `from` + `to`
 * back so the page reload preserves the user's choice).
 */

interface InviteFunnelRangeFilterProps {
  /** Current preset — derived by the parent from URL `?from`/`?to` or
   *  the funnel prop. Defaults to "30d" when not yet selected. */
  value: FunnelRangePreset
  /** Optional ISO date strings echoed by BE — pre-fill the custom
   *  popover when value === "custom". */
  initialFrom?: string
  initialTo?: string
  className?: string
}

function rangeForPreset(preset: "7d" | "30d"): { from: string; to: string } {
  const today = new Date()
  const days = preset === "7d" ? 6 : 29 // inclusive of today
  const from = new Date(today.getTime() - days * 24 * 3600_000)
  return {
    from: toIsoDate(from),
    to: toIsoDate(today),
  }
}

function toIsoDate(d: Date): string {
  // YYYY-MM-DD in local TZ — matches BE's brand-TZ bucketing on the
  // backend side (BE re-evaluates against brand TZ, not browser TZ).
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const dd = String(d.getDate()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}`
}

export function InviteFunnelRangeFilter({
  value,
  initialFrom = "",
  initialTo = "",
  className,
}: InviteFunnelRangeFilterProps) {
  const { t } = useTranslation()
  const [customOpen, setCustomOpen] = useState(false)
  const [from, setFrom] = useState(initialFrom)
  const [to, setTo] = useState(initialTo)
  // Base UI Popover has no Anchor component; the popover content anchors to
  // this ref (the visible "Custom" preset button) via `PopoverContent`'s
  // `anchor` prop. Ref-forwarding through PresetButton keeps the C14 contract:
  // without a real DOM node the popup would render at (0,0).
  const customButtonRef = useRef<HTMLButtonElement>(null)

  const applyPreset = (preset: FunnelRangePreset) => {
    if (preset === "custom") {
      setCustomOpen(true)
      return
    }
    const range = rangeForPreset(preset)
    router.reload({
      only: ["funnel"],
      data: { from: range.from, to: range.to },
      preserveUrl: true,
    })
  }

  const applyCustom = () => {
    if (!from || !to) return
    router.reload({
      only: ["funnel"],
      data: { from, to },
      preserveUrl: true,
    })
    setCustomOpen(false)
  }

  return (
    <div
      data-testid="invite-funnel-range-filter"
      className={cn("flex items-center gap-2", className)}
      role="group"
      aria-label={t("invites.funnel.range.label")}
    >
      {/*
        P4 review fix (review-frontend Minor #4): the popover positions
        relative to its anchor. The prior pattern wrapped a zero-dimension
        `sr-only` span as the trigger, which anchored the popover to a
        (0,0)-ish point and produced unpredictable placement across viewports.
        Anchoring the popover content to the visible Custom preset button (via
        `PopoverContent`'s `anchor` prop → `customButtonRef`, without making it
        the trigger — open/close stays controlled via the `applyPreset` flow)
        anchors the popover to the visible button. Tabs + button click flow
        stays unchanged. (OKS-616: Base UI has no `<PopoverAnchor>` part; the
        `anchor` prop replaces it.)
      */}
      <Popover open={customOpen} onOpenChange={setCustomOpen}>
        <div
          role="tablist"
          aria-label={t("invites.funnel.range.label")}
          className="bg-muted text-muted-foreground inline-flex h-9 items-center justify-center rounded-lg p-[3px]"
        >
          <PresetButton
            preset="7d"
            active={value === "7d"}
            onSelect={() => applyPreset("7d")}
            label={t("invites.funnel.range.last_7d")}
            testId="invite-funnel-range-7d"
          />
          <PresetButton
            preset="30d"
            active={value === "30d"}
            onSelect={() => applyPreset("30d")}
            label={t("invites.funnel.range.last_30d")}
            testId="invite-funnel-range-30d"
          />
          <PresetButton
            ref={customButtonRef}
            preset="custom"
            active={value === "custom"}
            onSelect={() => applyPreset("custom")}
            label={t("invites.funnel.range.custom")}
            testId="invite-funnel-range-custom"
          />
        </div>

        <PopoverContent
          className="flex flex-col gap-3"
          align="end"
          anchor={customButtonRef}
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="funnel-range-from">
              {t("invites.funnel.range.from")}
            </Label>
            <Input
              id="funnel-range-from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="funnel-range-to">
              {t("invites.funnel.range.to")}
            </Label>
            <Input
              id="funnel-range-to"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          <Button type="button" onClick={applyCustom} disabled={!from || !to}>
            {t("invites.funnel.range.apply")}
          </Button>
        </PopoverContent>
      </Popover>
    </div>
  )
}

interface PresetButtonProps {
  preset: FunnelRangePreset
  active: boolean
  onSelect: () => void
  label: string
  testId: string
  /**
   * BugFix C14: PresetButton MUST forward refs to the inner <button>.
   * The Custom preset's button is the popover anchor (passed to
   * `PopoverContent`'s `anchor` prop via `customButtonRef`). Without ref
   * forwarding the anchor has no DOM node, the popover renders at (0,0) of
   * the viewport, and the user sees no date picker — exactly the C14
   * reproduction. React 19 supports `ref` as a regular prop on function
   * components; passing it through to the `<button>` ref restores the anchor.
   * (OKS-616: migrated off Radix `<PopoverAnchor asChild>` Slot forwarding to
   * Base UI's `anchor` prop; the ref contract is unchanged.)
   */
  ref?: React.Ref<HTMLButtonElement>
}

// Exported for the C14 ref-forwarding regression guard (see the OKS-616 spec):
// jsdom computes no layout, so the anchor contract is verified by asserting the
// ref reaches the inner <button> directly, not via positioning.
export function PresetButton({
  active,
  onSelect,
  label,
  testId,
  ref,
}: PresetButtonProps) {
  return (
    <button
      ref={ref}
      type="button"
      role="tab"
      aria-selected={active}
      data-state={active ? "active" : "inactive"}
      data-testid={testId}
      onClick={onSelect}
      className={cn(
        "inline-flex h-[calc(100%-1px)] items-center justify-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium whitespace-nowrap transition-colors",
        active
          ? "bg-background text-foreground shadow-sm"
          : "text-muted-foreground hover:text-foreground",
      )}
    >
      {label}
    </button>
  )
}
